// Shared utility functions for rental operations
import { LateFeeDetailsWireSchema } from '../../api/wire-schemas';
import type { Equipment, Rental, RentalItem } from './types';
import { EquipmentCondition } from './types';

// Per-item return data for multi-item returns
export interface ReturnItemData {
  rentalItemId: string;
  equipment: Equipment;
  quantity: number;
  isReturning: boolean;
  returnCondition: EquipmentCondition;
  damageDescription: string;
  damageCharge: number;
}

// Constants
export const MS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * Check if rental has multiple items (vs legacy single-item)
 */
export function isMultiItemRental(rental: Rental): boolean {
  return Boolean(rental.items && rental.items.length > 0);
}

/**
 * Get items from rental - supports both multi-item and legacy single-item rentals
 */
export function getRentalItems(rental: Rental): RentalItem[] {
  if (rental.items && rental.items.length > 0) {
    return rental.items;
  }
  // Legacy single-item support
  if (rental.equipment) {
    const totalAmount =
      'totalAmount' in rental.pricing ? rental.pricing.totalAmount : rental.pricing.grandTotal;
    return [
      {
        id: `legacy-${rental.id}`,
        equipmentId: rental.equipment.id,
        equipment: rental.equipment,
        quantity: 1,
        dailyRate: rental.equipment.dailyRate,
        appliedRate: rental.equipment.dailyRate,
        itemTotal: totalAmount,
      },
    ];
  }
  return [];
}

/**
 * Initialize return items from rental with default values
 */
export function initReturnItems(rental: Rental): Map<string, ReturnItemData> {
  const items = getRentalItems(rental);
  const map = new Map<string, ReturnItemData>();

  items.forEach(item => {
    map.set(item.id, {
      rentalItemId: item.id,
      equipment: item.equipment,
      quantity: item.quantity,
      isReturning: true, // Default: all items returning
      returnCondition: item.condition ?? item.equipment.condition,
      damageDescription: '',
      damageCharge: 0,
    });
  });

  return map;
}

/**
 * Calculate deposit refund based on damage charges and late fees.
 * For partial returns, refund is proportional to the deposit value
 * of the returning items vs total deposit value.
 */
export function calculateDepositRefund(
  items: Map<string, ReturnItemData>,
  depositPaid: number,
  lateFee: number
): number {
  const allItems = Array.from(items.values());
  const returningItems = allItems.filter(item => item.isReturning);
  const totalDamage = returningItems.reduce((sum, item) => sum + item.damageCharge, 0);

  // All items returning → full deposit minus deductions
  if (returningItems.length === allItems.length) {
    return Math.max(0, depositPaid - totalDamage - lateFee);
  }

  // Partial return → proportional deposit based on equipment deposit value
  const totalDepositValue = allItems.reduce(
    (sum, item) => sum + item.equipment.depositAmount * item.quantity,
    0
  );
  const returningDepositValue = returningItems.reduce(
    (sum, item) => sum + item.equipment.depositAmount * item.quantity,
    0
  );
  const proportionalDeposit =
    totalDepositValue > 0
      ? Math.round(depositPaid * (returningDepositValue / totalDepositValue))
      : 0;

  return Math.max(0, proportionalDeposit - totalDamage - lateFee);
}

/**
 * Calculate days overdue from rental end date.
 * Accepts optional `now` parameter for testability.
 */
export function getDaysOverdue(rental: Rental, now: Date = new Date()): number {
  const endDate = new Date(rental.endDate);
  const diffTime = now.getTime() - endDate.getTime();
  return Math.max(0, Math.ceil(diffTime / MS_PER_DAY));
}

/**
 * Sanitize damage charge input - ensure non-negative integer
 */
export function sanitizeDamageCharge(value: string | number): number {
  const num = typeof value === 'string' ? Number(value) : value;
  if (isNaN(num)) return 0;
  return Math.max(0, Math.floor(num));
}

// ============================================
// Early Return / Korai visszavétel (Story 46-11)
// ============================================

/** Discount tier structure (ADR-048) */
export interface DiscountTier {
  minDays: number;
  maxDays: number | undefined;
  discountPercent: number;
  tierName: string;
}

/**
 * Default discount tiers per ADR-048.
 * NOTE: tenant-specifikus tier-ek BE-fetchelése jelenleg nincs bekötve (a korábbi
 * useRentalReturn hook aspirational volt és törölve lett) — ezek a defaultok élnek.
 */
export const DEFAULT_DISCOUNT_TIERS: DiscountTier[] = [
  { minDays: 0, maxDays: 6, discountPercent: 0, tierName: 'Rövid távú' },
  { minDays: 7, maxDays: 13, discountPercent: 10, tierName: '1 hetes' },
  { minDays: 14, maxDays: 20, discountPercent: 15, tierName: '2 hetes' },
  { minDays: 21, maxDays: 29, discountPercent: 25, tierName: '3 hetes' },
  { minDays: 30, maxDays: undefined, discountPercent: 30, tierName: 'Hosszú távú' },
];

/** Early return calculation result */
export interface EarlyReturnInfo {
  isEarlyReturn: boolean;
  originalDays: number;
  actualDays: number;
  originalTierName: string;
  originalDiscountPercent: number;
  actualTierName: string;
  actualDiscountPercent: number;
  discountChanged: boolean;
  explanation: string;
}

/**
 * Calculate inclusive calendar days between two dates.
 * Both start and end dates count (ADR-048).
 * Normalizes to UTC midnight to avoid DST drift (CR-46-11 #1).
 */
export function calculateCalendarDays(startDate: Date, endDate: Date): number {
  const start = Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const end = Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
  if (end < start) return 0;
  return Math.round((end - start) / MS_PER_DAY) + 1;
}

/**
 * Get applicable discount tier for a given number of rental days.
 * Handles undefined maxDays as unbounded (CR-46-11 #3, #10).
 */
export function getDiscountTier(days: number): DiscountTier {
  if (days < 0) {
    throw new Error(`Invalid rental days: ${days}`);
  }
  for (const tier of DEFAULT_DISCOUNT_TIERS) {
    if (days >= tier.minDays && (tier.maxDays === undefined || days <= tier.maxDays)) {
      return tier;
    }
  }
  const fallback = DEFAULT_DISCOUNT_TIERS[0];
  if (!fallback) {
    throw new Error('No discount tiers configured');
  }
  return fallback;
}

/**
 * Check if a return is early and calculate discount tier impact.
 * Returns early return info if the return date is before the rental end date.
 */
export function calculateEarlyReturn(
  rental: Rental,
  returnDate: Date = new Date()
): EarlyReturnInfo {
  const startDate = new Date(rental.startDate);
  const endDate = new Date(rental.endDate);

  const originalDays = calculateCalendarDays(startDate, endDate);
  const actualDays = calculateCalendarDays(startDate, returnDate);
  // Compare date-only (UTC midnight) to avoid timezone edge cases (CR-46-11 #8)
  const returnUTC = Date.UTC(returnDate.getFullYear(), returnDate.getMonth(), returnDate.getDate());
  const endUTC = Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
  const isEarly = returnUTC < endUTC;

  const originalTier = getDiscountTier(originalDays);
  const actualTier = getDiscountTier(actualDays);
  const discountChanged = originalTier.discountPercent !== actualTier.discountPercent;

  let explanation: string;
  if (!isEarly) {
    explanation = 'A bérlés nem korai visszavétel.';
  } else if (!discountChanged) {
    explanation = `Korai visszavétel (${actualDays}/${originalDays} nap), de a kedvezmény sáv nem változott.`;
  } else {
    explanation =
      `Korai visszavétel: ${originalDays} nap → ${actualDays} nap. ` +
      `Kedvezmény: ${originalTier.discountPercent}% (${originalTier.tierName}) → ` +
      `${actualTier.discountPercent}% (${actualTier.tierName}).`;
  }

  return {
    isEarlyReturn: isEarly,
    originalDays,
    actualDays,
    originalTierName: originalTier.tierName,
    originalDiscountPercent: originalTier.discountPercent,
    actualTierName: actualTier.tierName,
    actualDiscountPercent: actualTier.discountPercent,
    discountChanged,
    explanation,
  };
}

// ============================================
// Rental Extension / Bérlés hosszabbítás (Story 46-12)
// ============================================

/** VAT rate matching backend (rental.service.ts VAT_RATE) */
const HU_VAT_RATE = 0.27;

/** Extension preview result */
export interface ExtensionPreview {
  additionalDays: number;
  originalEndDate: string;
  newEndDate: string;
  dailyRate: number;
  additionalAmount: number;
  vatAmount: number;
  additionalAmountWithVat: number;
  originalTierName: string;
  newTierName: string;
  discountChanged: boolean;
}

/**
 * Format Date to YYYY-MM-DD using local date components (avoids UTC timezone shift).
 */
function formatDateLocal(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Calculate extension preview for a rental.
 * Returns null if the new end date is not after the current end date.
 * NOTE: This is an approximate preview. The backend recalculates the full
 * pricing (including discount tier adjustments) on the actual extension call.
 */
export function calculateExtensionPreview(
  currentEndDate: string,
  startDate: string,
  newEndDate: Date,
  dailyRate: number
): ExtensionPreview | null {
  const currentEnd = new Date(currentEndDate);
  const newEndUTC = Date.UTC(newEndDate.getFullYear(), newEndDate.getMonth(), newEndDate.getDate());
  const currentEndUTC = Date.UTC(
    currentEnd.getFullYear(),
    currentEnd.getMonth(),
    currentEnd.getDate()
  );

  if (newEndUTC <= currentEndUTC) return null;

  const start = new Date(startDate);
  const additionalDays = Math.round((newEndUTC - currentEndUTC) / MS_PER_DAY);
  const additionalAmount = additionalDays * dailyRate;
  const vatAmount = Math.round(additionalAmount * HU_VAT_RATE);
  const additionalAmountWithVat = additionalAmount + vatAmount;

  const originalTotalDays = calculateCalendarDays(start, currentEnd);
  const newTotalDays = calculateCalendarDays(start, newEndDate);
  const originalTier = getDiscountTier(originalTotalDays);
  const newTier = getDiscountTier(newTotalDays);

  return {
    additionalDays,
    originalEndDate: currentEndDate,
    newEndDate: formatDateLocal(newEndDate),
    dailyRate,
    additionalAmount,
    vatAmount,
    additionalAmountWithVat,
    originalTierName: originalTier.tierName,
    newTierName: newTier.tierName,
    discountChanged: originalTier.discountPercent !== newTier.discountPercent,
  };
}

/**
 * Késedelmi-díj bontás-nézet a wire-`LateFeeDetails`-ből (39-kör FE-4).
 *
 * DRY-money: minden pénz-érték a wire-ből jön (az `amount` a BE-terhelt összeg,
 * a `dailyLateFeeRate` a BE által max-olt effektív napi tétel) — a FE nem számol
 * díjat, az `isCapped` predikátum csak wire-értékek összehasonlítása.
 *
 * Graceful degradálás (#718-guard): ha a bemenet hiányzik vagy régi-shape-ű
 * (a #738 előtti breakdown: lateFeeRate/effectiveDays), `null` jön vissza —
 * a hívó a mai skalár-sort mutatja bontás nélkül, merge-order-függetlenül.
 */
export interface LateFeeBreakdownView {
  daysOverdue: number;
  dailyLateFeeRate: number;
  amount: number;
  isCapped: boolean;
  isLegacyPercentageModel: boolean;
  isWaived: boolean;
}

export function getLateFeeBreakdownView(raw: unknown): LateFeeBreakdownView | null {
  const parsed = LateFeeDetailsWireSchema.safeParse(raw);
  if (!parsed.success) return null;
  const details = parsed.data;
  if (details.daysOverdue <= 0 || details.amount <= 0) return null;
  return {
    daysOverdue: details.daysOverdue,
    dailyLateFeeRate: details.dailyLateFeeRate,
    amount: details.amount,
    isCapped: details.dailyLateFeeRate * details.daysOverdue > details.maxLateFee,
    isLegacyPercentageModel: details.feeModel === 'DAILY_RATE_PERCENTAGE',
    isWaived: details.isWaived === true,
  };
}

/**
 * Settlement-preview request-tételek összeállítása (40-kör, #744 request-pin):
 * a visszahozott gépek damage-inputja + az opcionális tartozék-hiány / tisztítás-
 * override extrák merge-e. Csak a pozitív értékek mennek a wire-re (a 0/üres
 * mező = nincs input → a BE-default érvényesül, pl. tisztítás-alapdíj).
 * 0 FE-számítás: az értékek pultos-inputok, a kalkuláció a BE-ben történik.
 */
export interface SettlementExtras {
  missingAccessoryCharge?: number | undefined;
  cleaningFeeOverride?: number | undefined;
}

export function buildSettlementItems(
  returningItems: ReturnItemData[],
  extras: Map<string, SettlementExtras>
): Array<{
  rentalItemId: string;
  damageAmount?: number;
  missingAccessoryCharge?: number;
  cleaningFeeOverride?: number;
}> {
  return returningItems.map(item => {
    const extra = extras.get(item.rentalItemId);
    return {
      rentalItemId: item.rentalItemId,
      ...(item.damageCharge > 0 ? { damageAmount: item.damageCharge } : {}),
      ...(extra?.missingAccessoryCharge != null && extra.missingAccessoryCharge > 0
        ? { missingAccessoryCharge: extra.missingAccessoryCharge }
        : {}),
      ...(extra?.cleaningFeeOverride != null && extra.cleaningFeeOverride > 0
        ? { cleaningFeeOverride: extra.cleaningFeeOverride }
        : {}),
    };
  });
}

/**
 * W1 (40-kör, Javo-ACK): a processReturn settle-mezőinek összeállítása.
 * - settleMode CSAK halasztásnál megy a wire-re (NOW = BE-default, elhagyva);
 * - halasztás CSAK részleges visszavételnél értelmezett (záró-gépnél a BE
 *   zárás-invariánsa atomian rendez — a UI fel sem ajánlja) és CSAK ha nincs
 *   forceImmediateSettle (R-2 preAuth-hard-guard);
 * - returnedItemIds CSAK részleges multi-item visszavételnél (elhagyva = minden
 *   item, legacy-ekvivalens; legacy single-item id-k nem garantáltan uuid-k).
 */
export function buildReturnSettleFields(input: {
  isPartialReturn: boolean;
  isMultiItem: boolean;
  settleMode: 'NOW' | 'AT_CLOSE';
  forceImmediate: boolean;
  returningItems: ReturnItemData[];
}): { settleMode?: 'AT_CLOSE'; returnedItemIds?: string[] } {
  const { isPartialReturn, isMultiItem, settleMode, forceImmediate, returningItems } = input;
  return {
    ...(isPartialReturn && !forceImmediate && settleMode === 'AT_CLOSE'
      ? { settleMode: 'AT_CLOSE' as const }
      : {}),
    ...(isPartialReturn && isMultiItem
      ? { returnedItemIds: returningItems.map(i => i.rentalItemId) }
      : {}),
  };
}
